import type { Division, SourceMeta } from '../types';

/**
 * Slice grid maths.
 *
 * The grid is derived, never hand-edited: bpm + offset + bars + division fully
 * determine where the cuts land. `snapToTransients` then nudges each cut to the
 * nearest detected onset (within half a slice) so chopped breaks stay tight
 * even when the source drifts off the metronome.
 */

export function sliceCount(source: SourceMeta, beatsPerBar: number): number {
  return Math.max(1, Math.round(source.bars * beatsPerBar * source.division));
}

export function secondsPerSlice(source: SourceMeta): number {
  return 60 / source.bpm / source.division;
}

export function buildSliceEdges(source: SourceMeta, beatsPerBar: number): number[] {
  const count = sliceCount(source, beatsPerBar);
  const step = secondsPerSlice(source);
  const edges: number[] = [];
  for (let i = 0; i <= count; i++) {
    let t = source.offset + i * step;
    if (source.snapToTransients && source.transients.length && i > 0 && i < count) {
      t = snap(t, source.transients, step * 0.4);
    }
    edges.push(Math.max(0, Math.min(source.duration, t)));
  }
  // Guarantee monotonic edges even if snapping crossed two cuts over.
  for (let i = 1; i < edges.length; i++) {
    if (edges[i] <= edges[i - 1]) edges[i] = Math.min(source.duration, edges[i - 1] + 0.002);
  }
  return edges;
}

function snap(t: number, transients: number[], tolerance: number): number {
  let best = t;
  let bestDist = tolerance;
  // transients are sorted; a linear scan is fine at these sizes
  for (const tr of transients) {
    const d = Math.abs(tr - t);
    if (d < bestDist) {
      bestDist = d;
      best = tr;
    }
    if (tr > t + tolerance) break;
  }
  return best;
}

/** Best-guess bar count for a freshly imported loop. */
export function inferBars(duration: number, bpm: number, beatsPerBar: number): number {
  const barSeconds = (60 / bpm) * beatsPerBar;
  const raw = duration / barSeconds;
  // Loops are almost always a power-of-two bar count; snap when we're close.
  for (const candidate of [1, 2, 4, 8, 16, 32]) {
    if (Math.abs(raw - candidate) / candidate < 0.08) return candidate;
  }
  return Math.max(1, Math.round(raw * 2) / 2);
}

export const DIVISIONS: { value: Division; label: string }[] = [
  { value: 1, label: '1/4 · beats' },
  { value: 2, label: '1/8' },
  { value: 3, label: '1/8T' },
  { value: 4, label: '1/16' },
  { value: 6, label: '1/16T' },
  { value: 8, label: '1/32' },
  { value: 12, label: '1/32T' },
  { value: 16, label: '1/64' },
];

export function divisionLabel(d: Division): string {
  return DIVISIONS.find((x) => x.value === d)?.label ?? `${d}/beat`;
}
